/**
 * Orders client (hand-rolled).
 *
 * Transactional layer over the deployed /orders endpoints. An order is minted
 * when a seller accepts a buyer's offer; both parties can view it, the seller
 * can settle a `draft` order, and either party can cancel a `draft`. Orval is
 * not regenerated for these yet, so the DTO + clients are hand-rolled on the
 * shared axios mutator (src/api/client.ts → fseAxios), mirroring the other
 * hand-rolled clients.
 *
 * MONEY: *Micro fields are micro-ZARP (1e-6 ZARP) integer STRINGS — display
 * only (microToZarp + <Money>), never Number() for math. The list endpoint
 * returns `{ data: [...], pagination }`; single + mutations return the DTO.
 */
import { fseAxios } from './client';
import type {
  OrderDto as GeneratedOrderDto,
  OrderDtoStatus,
  OrderListDto,
  OrderListDtoPagination,
  OrdersControllerListParams,
} from './generated/models';

// Kept in sync with src/api/client.ts (module-private there) — same pattern as
// src/api/photos.ts / marketplace.ts. The delivery-photo endpoint streams raw
// image bytes behind auth, so it can't go through the JSON `fseAxios` mutator
// (it unwraps response.data / expects JSON) nor straight into `<img src>` (a
// plain <img> won't send the bearer). If client.ts changes the key/fallback,
// update this helper too.
const PHOTO_DEFAULT_BASE_URL = 'https://api.foodsecurityexchange.cloud';
const PHOTO_ACCESS_KEY = 'fse_access_token';

function resolvePhotoBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_FSE_API_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return PHOTO_DEFAULT_BASE_URL;
}

function photoAuthHeader(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const token = window.localStorage.getItem(PHOTO_ACCESS_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Proof-of-delivery fields. The deployed/regenerated OrderDto carries these
// (status reaches `delivered`/`settled`; `deliveredAt`, `deliveryLat`,
// `deliveryLng`, `hasDeliveryPhoto`), but until the orval regen lands these are
// declared here so the order-detail enrichment compiles against the live wire
// contract. Once the generated OrderDto includes them this augmentation is a
// harmless superset and can be folded away. All four are null/false-safe.
interface OrderDeliveryFields {
  /** When the seller recorded delivery (ISO); null until delivered. @nullable */
  deliveredAt?: string | null;
  /** Captured delivery latitude (decimal degrees); null when not captured. @nullable */
  deliveryLat?: number | null;
  /** Captured delivery longitude (decimal degrees); null when not captured. @nullable */
  deliveryLng?: number | null;
  /** Whether a proof-of-delivery photo is attached to this order. */
  hasDeliveryPhoto?: boolean;
}

/**
 * Order as the screens consume it: the generated DTO plus the proof-of-delivery
 * fields (see OrderDeliveryFields). A future orval regen that adds these to the
 * generated OrderDto keeps this assignable (superset), so screens need no change.
 */
export type OrderDto = GeneratedOrderDto & OrderDeliveryFields;

// DTOs/query are the single source of truth in the generated client (TD-124
// regen), re-exported under the names the screens use. The client functions stay
// hand-rolled because they return the unwrapped body (fseAxios) screens consume.
export type OrderStatus = OrderDtoStatus;
export type OrdersPagination = OrderListDtoPagination;
export type OrderListResponse = OrderListDto;
export type ListOrdersQuery = OrdersControllerListParams;

/**
 * List orders for the caller — the server returns those where the caller is
 * either buyer or seller. Optional status filter + pagination.
 */
export function listOrders(
  query: ListOrdersQuery = {},
  signal?: AbortSignal,
): Promise<OrderListResponse> {
  return fseAxios<OrderListResponse>({
    url: '/orders',
    method: 'GET',
    params: query,
    signal,
  });
}

/** Read a single order by id (buyer or seller). */
export function getOrder(id: string, signal?: AbortSignal): Promise<OrderDto> {
  return fseAxios<OrderDto>({
    url: `/orders/${id}`,
    method: 'GET',
    signal,
  });
}

/** Mark a draft order settled (seller only). */
export function settleOrder(id: string): Promise<OrderDto> {
  return fseAxios<OrderDto>({
    url: `/orders/${id}/settle`,
    method: 'PATCH',
  });
}

/** Cancel a draft order (either party). */
export function cancelOrder(id: string): Promise<OrderDto> {
  return fseAxios<OrderDto>({
    url: `/orders/${id}/cancel`,
    method: 'PATCH',
  });
}

/**
 * Fetch the raw proof-of-delivery image bytes for an order with auth and turn
 * them into an object URL suitable for `<img src>`. AUTHED — the order's buyer
 * or seller party may fetch (the server is the authority); a plain <img src>
 * would NOT send the bearer, so we mirror src/api/photos.ts: built-in `fetch`
 * with the Authorization header → blob → object URL. The caller OWNS the URL and
 * MUST `URL.revokeObjectURL` it on cleanup to avoid leaking blobs. Throws on a
 * non-2xx (e.g. 404 when the order has no delivery photo) — callers should fall
 * back to a placeholder on error.
 */
export async function fetchDeliveryPhotoObjectUrl(orderId: string): Promise<string> {
  const res = await fetch(`${resolvePhotoBaseUrl()}/orders/${orderId}/delivery-photo`, {
    method: 'GET',
    headers: { ...photoAuthHeader() },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
